import * as ents from '@attain-apps/entities'
import { TypeChecker } from '@attain-apps/type-guards'

/**
 * {@link https://ngrx.io/docs State} of this feature
 */
export interface State {
  error?: Error
  functionalUsage?: ents.extra.space.Record.FunctionalUsage
  editingFunctionalUsage?: Partial<ents.extra.space.Record.FunctionalUsage.Update>
  isLoading: boolean
}

export namespace State {
  export const INITIAL: Readonly<State> = {
    editingFunctionalUsage: undefined,
    error: undefined,
    functionalUsage: undefined,
    isLoading: false,
  }

  /**
   * Update the editing state with new values. Only the provided fields will be
   * updated, the rest will be left as is. This allows for multiple fields to be
   * updated independently without affecting each other.
   */
  export function update(
    state: State,
    updates: OneOf<Partial<ents.extra.space.Record.FunctionalUsage.Update>>,
  ): State {
    const {
      functionalizationMethodology,
      functionalizations,
      serviceCenterId,
    } = updates as FlattenUnion<typeof updates>

    if (TypeChecker.isNotNullish(functionalizationMethodology)) {
      return {
        ...state,
        editingFunctionalUsage: {
          functionalizationMethodology,
        },
      }
    }

    if (TypeChecker.isNotNullish(functionalizations)) {
      return {
        ...state,
        editingFunctionalUsage: {
          functionalizationMethodology:
            state.editingFunctionalUsage?.functionalizationMethodology,
          functionalizations,
        },
      }
    }

    if (TypeChecker.isNotNullish(serviceCenterId)) {
      return {
        ...state,
        editingFunctionalUsage: {
          functionalizationMethodology:
            state.editingFunctionalUsage?.functionalizationMethodology,
          serviceCenterId,
        },
      }
    }

    return state
  }

  /**
   * Initialize editing state with a sanitized copy of the current functional
   * usage. For Manual methodology, strips corrupted leftover exclusive-function
   * (Vacant/Unassignable) entries so Save is not permanently blocked by data
   * the UI cannot display or edit. Does not mutate `state.functionalUsage`
   * itself, since warnings/read views rely on seeing the true corrupted data.
   */
  export function beginUpdating(state: State): State {
    if (!state.functionalUsage) {
      return { ...state, editingFunctionalUsage: undefined }
    }

    const editingFunctionalUsage = { ...state.functionalUsage }

    if (
      editingFunctionalUsage.functionalizationMethodology ===
        ents.extra.space.Record.FunctionalUsage.Methodology.Manual &&
      editingFunctionalUsage.functionalizations
    ) {
      editingFunctionalUsage.functionalizations =
        editingFunctionalUsage.functionalizations.filter(
          f => !ents.extra.space.Function.Standard.isExclusiveId(f.functionId),
        )
    }

    return { ...state, editingFunctionalUsage }
  }

  /** Update a specific functionalization's percentage or add it if it doesn't exist */
  export function updateFunctionalization(
    state: State,
    { functionId, percent }: ents.extra.space.Functionalization,
  ): State {
    const existing = state.editingFunctionalUsage?.functionalizations ?? []
    const idx = existing.findIndex(f => f.functionId === functionId)

    // If found, replace it in place; otherwise append it to the list
    const updated = [...existing]
    if (idx >= 0) {
      updated.splice(idx, 1, { functionId, percent })
    } else {
      updated.push({ functionId, percent })
    }

    return update(state, { functionalizations: updated })
  }
}
